import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { ChillToggleButton } from 'App/Map/PanelDetails/SectionTitleBars/ChillTitleChildren/ChillToggleButton'
import { DropdownSelectorPoint } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorPoint/DropdownSelectorPoint'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { StackedChartSection, TChartSeries } from 'components/StackedChart/types'
import moment from 'moment-timezone'
import type { TRGBAColorWith1AtTheEnd } from 'settings/colors'
import type { TChillMarVsMay, TChillSepVsNov } from 'stores/detailsPanelStore/detailsPanelStore'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import type {
  TPointCategory,
  TSelectedFieldAssetsStoreState,
} from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedPointHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { propertyIsSouthernHemisphere } from 'utils/propertyIsSouthernHemisphere'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getXDateFormat } from '../_utils/getXDateFormat'
import { makeCompareSeasonsSeriesFromRegularSeries } from '../_utils/makeCompareSeasonsSeriesFromRegularSeries'

const pointCategory: TPointCategory = 'weatherPoint'
const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_CHILL_DETAILS' })
const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' }

const getValueTypes = (isSouthernHemisphere: boolean | undefined) => {
  const valuesRequested: VV.DomainTypes.Chill.TTimeseriesValueTypeKeysMerged[] = isSouthernHemisphere
    ? [
        'chillCumulativeHoursBetween32and45FMAR',
        'chillCumulativeHoursBelow45FMAR',
        'chillCumulativePortionsMAR',
        'chillCumulativeHoursBetween32and45FMAY',
        'chillCumulativeHoursBelow45FMAY',
        'chillCumulativePortionsMAY',
      ]
    : [
        'chillCumulativeHoursBetween32and45FSEP',
        'chillCumulativeHoursBelow45FSEP',
        'chillCumulativePortionsSEP',
        'chillCumulativeHoursBetween32and45FNOV',
        'chillCumulativeHoursBelow45FNOV',
        'chillCumulativePortionsNOV',
      ]

  return valuesRequested
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.chill || !selectedFieldAssets[pointCategory]) return {}

  const isSouthernHemisphere = propertyIsSouthernHemisphere()
  const valueTypes = getValueTypes(isSouthernHemisphere)

  if (
    !doesSelectedPointHaveValueTypes({
      valuesTimeseries: valueTypes,
      pointCategory,
    })
  )
    return {}

  const valuesRequested = Object.fromEntries(
    valueTypes.map((valueType) => {
      return [valueType, preferredAggregationInterval]
    }),
  )

  return {
    points: {
      lngLats: [selectedFieldAssets[pointCategory]],
      valuesRequested,
    },
  }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const isSouthernHemisphere = propertyIsSouthernHemisphere()
  const timezone = getTimezoneForSelectedPropertyOrRegion()

  const commonReturnItems = {
    title: unitConverter.chillCumulativeHoursBelow45FSEP().categoryTitleWithoutUnit(),
    titleToggle: <ChillToggleButton isSouthernHemisphere={!!isSouthernHemisphere} showMonthsToggle={false} />,
    // titleChildren: ChillTitleChildren({
    //   isSouthernHemisphere: !!isSouthernHemisphere,
    //   showMonthsToggle: false,
    // }),
    fieldAssetPicker: (
      <DropdownSelectorPoint
        pointCategory={pointCategory}
        valuesTimeseriesToFilterOn={getValueTypes(isSouthernHemisphere)}
      />
    ),
    id: 'stackem-chill',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const { chillMarVsMay, chillSepVsNov, compareSeasonsInterval } = detailsPanelStore.getState()
  const stationLngLat = String(selectedFieldAssetsStore.getState()[pointCategory])

  const chillMonthToUse: TChillMarVsMay | TChillSepVsNov = (() => {
    if (isSouthernHemisphere && chillMarVsMay === 'MAR') return 'MAR'

    if (isSouthernHemisphere && chillMarVsMay === 'MAY') return 'MAY'

    if (!isSouthernHemisphere && chillSepVsNov === 'SEP') return 'SEP'

    return 'NOV'
  })()

  const chillCumulativeHoursBelow45FToUse:
    | 'chillCumulativeHoursBelow45FSEP'
    | 'chillCumulativeHoursBelow45FNOV'
    | 'chillCumulativeHoursBelow45FMAR'
    | 'chillCumulativeHoursBelow45FMAY' = `chillCumulativeHoursBelow45F${chillMonthToUse}`

  const chillCumulativeHoursBetween32and45FToUse:
    | 'chillCumulativeHoursBetween32and45FSEP'
    | 'chillCumulativeHoursBetween32and45FNOV'
    | 'chillCumulativeHoursBetween32and45FMAR'
    | 'chillCumulativeHoursBetween32and45FMAY' = `chillCumulativeHoursBetween32and45F${chillMonthToUse}`

  const chillCumulativePortionsToUse:
    | 'chillCumulativePortionsSEP'
    | 'chillCumulativePortionsNOV'
    | 'chillCumulativePortionsMAR'
    | 'chillCumulativePortionsMAY' = `chillCumulativePortions${chillMonthToUse}`

  const unitConvertersToUse: Record<
    'chillCumulativeHoursBetween32and45F' | 'chillCumulativeHoursBelow45F' | 'chillCumulativePortions',
    typeof unitConverter.chillCumulativeHoursBelow45FMAR
  > = {
    chillCumulativeHoursBetween32and45F: unitConverter?.[chillCumulativeHoursBetween32and45FToUse],
    chillCumulativeHoursBelow45F: unitConverter?.[chillCumulativeHoursBelow45FToUse],
    chillCumulativePortions: unitConverter?.[chillCumulativePortionsToUse],
  }

  const chillHoursSeries: TChartSeries[] = []

  const chillHoursSeriesBelow45: TChartSeries & { color?: TRGBAColorWith1AtTheEnd } = {
    name: unitConvertersToUse.chillCumulativeHoursBelow45F().shortTitle(),
    tooltip: {
      valueDecimals: unitConvertersToUse.chillCumulativeHoursBelow45F().defaultNumberOfDecimalPlaces(),
    },
    yAxis: 0,
    data: (
      data?.points?.[stationLngLat]?.values?.[chillCumulativeHoursBelow45FToUse]?.[0]?.timeseries ?? []
    ).map((d) => [+new Date(d.timestamp), unitConvertersToUse.chillCumulativeHoursBelow45F(d.value).value()]),
    type: 'line',
    id: 'chillHours_below45',
  }

  chillHoursSeries.push(chillHoursSeriesBelow45)

  if (compareSeasonsInterval) {
    chillHoursSeries.push(
      makeCompareSeasonsSeriesFromRegularSeries(chillHoursSeriesBelow45, {
        data: (
          compareSeasonsData?.points?.[stationLngLat]?.values?.[chillCumulativeHoursBelow45FToUse]?.[0]
            ?.timeseries ?? []
        ).map((d) => [
          +new Date(d.timestamp),
          unitConvertersToUse.chillCumulativeHoursBelow45F(d.value).value(),
        ]),
      }),
    )
  }

  const chillHoursSeriesBetween32and45: TChartSeries & { color?: TRGBAColorWith1AtTheEnd } = {
    name: unitConvertersToUse.chillCumulativeHoursBetween32and45F().shortTitle(),
    tooltip: {
      valueDecimals: unitConvertersToUse.chillCumulativeHoursBetween32and45F().defaultNumberOfDecimalPlaces(),
    },
    yAxis: 0,
    data: (
      data?.points?.[stationLngLat]?.values?.[chillCumulativeHoursBetween32and45FToUse]?.[0]?.timeseries ?? []
    ).map((d) => [
      +new Date(d.timestamp),
      unitConvertersToUse.chillCumulativeHoursBetween32and45F(d.value).value(),
    ]),
    type: 'line',
    id: 'chillHours_between32and45',
  }

  chillHoursSeries.push(chillHoursSeriesBetween32and45)

  if (compareSeasonsInterval) {
    chillHoursSeries.push(
      makeCompareSeasonsSeriesFromRegularSeries(chillHoursSeriesBetween32and45, {
        data: (
          compareSeasonsData?.points?.[stationLngLat]?.values?.[chillCumulativeHoursBetween32and45FToUse]?.[0]
            ?.timeseries ?? []
        ).map((d) => [
          +new Date(d.timestamp),
          unitConvertersToUse.chillCumulativeHoursBetween32and45F(d.value).value(),
        ]),
      }),
    )
  }

  const chillPortionsSeries: TChartSeries[] = []

  const chillPortionsCumulativeSeries: TChartSeries & { color?: TRGBAColorWith1AtTheEnd } = {
    name: unitConvertersToUse.chillCumulativePortions().shortTitle(),
    tooltip: {
      valueDecimals: unitConvertersToUse.chillCumulativePortions().defaultNumberOfDecimalPlaces(),
    },
    yAxis: 0,
    data: (data?.points?.[stationLngLat]?.values?.[chillCumulativePortionsToUse]?.[0]?.timeseries ?? []).map(
      (d) => [+new Date(d.timestamp), unitConvertersToUse.chillCumulativePortions(d.value).value()],
    ),
    type: 'line',
    id: 'chillPortions',
  }

  chillPortionsSeries.push(chillPortionsCumulativeSeries)

  if (compareSeasonsInterval) {
    chillPortionsSeries.push(
      makeCompareSeasonsSeriesFromRegularSeries(chillPortionsCumulativeSeries, {
        data: (
          compareSeasonsData?.points?.[stationLngLat]?.values?.[chillCumulativePortionsToUse]?.[0]
            ?.timeseries ?? []
        ).map((d) => [+new Date(d.timestamp), unitConvertersToUse.chillCumulativePortions(d.value).value()]),
      }),
    )
  }

  const xDateFormat = getXDateFormat()

  const forecastStartsAt =
    data?.points?.[stationLngLat]?.values?.[chillCumulativePortionsToUse]?.[0]?.metadata?.forecastStartsAt

  let firstForecastTimestamp: number = +new Date()

  if (forecastStartsAt) {
    firstForecastTimestamp = +moment.tz(forecastStartsAt, timezone).toDate()
  }

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig: {
          yAxis: { min: 0 },
          semiosHighchartsAdditions: {
            id: `${commonReturnItems.id}_hours`,
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          series: chillHoursSeries,
          tooltip: {
            xDateFormat,
          },
        },
      },
      {
        chartConfig: {
          yAxis: { min: 0 },
          semiosHighchartsAdditions: {
            id: `${commonReturnItems.id}_portions`,
            firstForecastTimestamp,
          },
          chart: {
            type: 'line',
          },
          series: chillPortionsSeries,
          tooltip: {
            xDateFormat,
          },
        },
      },
    ],
    titleToggle: <ChillToggleButton isSouthernHemisphere={!!isSouthernHemisphere} showMonthsToggle={true} />,
  }
}
